<template>
  <div id="johkumax">
    <h3>{{ _('Johku Max') }}</h3>
    <p v-if="loading && !loaded"><i class="fa fa-fw fa-spinner fa-spin"></i> {{ _('Loading...') }}</p>
    <template v-else-if="loaded">
      <div class="alert alert-danger" v-if="lastError && !pending">{{ lastError }}</div>
      <div class="alert alert-info" v-if="pending">
        <i class="fa fa-fw fa-spinner fa-spin"></i> {{ pendingText }}
      </div>

      <template v-if="isActive">
        <div class="alert alert-danger" v-if="isSuspended">{{ _('Johku Max is suspended because the latest payment is overdue. It will be restored once the payment is received.') }}</div>
        <dl class="dl-horizontal">
          <dt>{{ _('Status') }}</dt>
          <dd>
            <span v-if="isSuspended" class="label label-danger">{{ _('Suspended') }}</span>
            <span v-else class="label label-success">{{ _('Active') }}</span>
          </dd>
          <dt>{{ _('Billing period') }}</dt>
          <dd>{{ termName(subscription.term) }}</dd>
          <dt>{{ _('Extra users') }}</dt>
          <dd>
            {{ subscription.seats }}
            <span v-if="subscription.scheduledSeats !== null" class="text-muted">({{ _('%s from the next billing period').replace('%s', subscription.scheduledSeats) }})</span>
          </dd>
          <dt>{{ _('Current period') }}</dt>
          <dd>{{ formatDate(subscription.periodStart) }}–{{ formatLastDay(subscription.periodEnd) }}</dd>
          <dt>{{ _('Paid until') }}</dt>
          <dd>{{ formatLastDay(subscription.validUntil) }}</dd>
          <dt v-if="!subscription.cancelAtPeriodEnd">{{ _('Next invoice') }}</dt>
          <dd v-if="!subscription.cancelAtPeriodEnd">{{ formatDate(subscription.nextBilling) }}</dd>
        </dl>

        <h4>{{ _('Users') }}</h4>
        <p>{{ _('The license includes %s users, and you can buy more. A user gets access the first time they use the Johku admin tools with an AI assistant, if there is room. Remove a user to give their place to someone else.').replace('%s', includedSeats) }}</p>
        <p v-if="seatList">
          <strong>{{ _('%1$s of %2$s users in use').replace('%1$s', seatList.used).replace('%2$s', seatList.quota || 0) }}</strong>
        </p>
        <p v-if="seatList && !seatList.seats.length" class="text-muted">{{ _('No one has used the Johku admin tools yet.') }}</p>
        <table class="table table-condensed" v-if="seatList && seatList.seats.length">
          <thead>
            <tr>
              <th>{{ _('Name') }}</th>
              <th>{{ _('Email') }}</th>
              <th>{{ _('Last used') }}</th>
              <th><span class="sr-only">{{ _('Actions') }}</span></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="seat in seatList.seats" :key="seat.id">
              <td>
                {{ seat.name }}
                <span v-if="!seat.active" class="label label-warning">{{ _('No access, over the user limit') }}</span>
              </td>
              <td>{{ seat.email }}</td>
              <td>{{ formatDateTime(seat.lastUsed) }}</td>
              <td class="text-right">
                <button class="btn btn-xs btn-default" type="button" v-on:click="removeSeatDialog(seat)" :disabled="busy"><i class="fa fa-fw fa-user-times"></i> {{ _('Remove') }}</button>
              </td>
            </tr>
          </tbody>
        </table>
        <p v-if="subscription.paymentUrl">
          <a class="btn btn-primary" :href="subscription.paymentUrl" target="_blank" rel="noopener"><i class="fa fa-fw fa-credit-card"></i> {{ _('Pay by card') }}</a>
        </p>

        <div class="alert alert-warning" v-if="subscription.cancelAtPeriodEnd">
          {{ _('Johku Max will end on %s and will not be renewed.').replace('%s', formatLastDay(subscription.validUntil)) }}
        </div>

        <h4>{{ _('Change the number of extra users') }}</h4>
        <p>{{ _('Added users are charged for the rest of the current period on your next monthly invoice. Removed users are taken off at the start of the next billing period; there are no refunds.') }}</p>
        <div class="form-inline">
          <div class="form-group">
            <label for="johkumaxSeats" class="sr-only">{{ _('Extra users') }}</label>
            <input type="number" class="form-control" id="johkumaxSeats" min="0" :max="maxSeats" v-model.number="newSeats" :disabled="busy" />
          </div>
          <button class="btn btn-default" type="button" v-on:click="changeSeats" :disabled="busy || !seatsChanged"><i class="fa fa-fw fa-users"></i> {{ _('Change users') }}</button>
        </div>
        <p class="help-block" v-if="seatsChanged && newSeats > subscription.seats && offer.seat">
          {{ _('About %s (excluding VAT) will be added to your next invoice.').replace('%s', formatPrice(seatChangeEstimate)) }}
        </p>

        <h4>{{ _('Renewal') }}</h4>
        <button v-if="!subscription.cancelAtPeriodEnd" class="btn btn-warning" type="button" v-on:click="cancelDialog" :disabled="busy"><i class="fa fa-fw fa-ban"></i> {{ _('Cancel Johku Max') }}</button>
        <button v-else class="btn btn-success" type="button" v-on:click="setCancel(false)" :disabled="busy"><i class="fa fa-fw fa-undo"></i> {{ _('Keep Johku Max') }}</button>
      </template>

      <template v-else-if="!pending">
        <p v-if="!offer || !offer.available" class="text-muted">{{ _('The Johku Max order form could not be loaded. Please try again later.') }}</p>
        <template v-else>
          <p><span class="label label-default">{{ _('Not ordered') }}</span> {{ _('Johku Max is not in use in this shop yet. You can order it here.') }}</p>
          <p>{{ _('Johku Max is billed in advance. A yearly term is billed right away for 12 months; a monthly term is billed right away for the rest of this month and then on the first day of each month.') }}</p>
          <div class="form-group">
            <label>{{ _('Choose a billing period') }}</label>
            <div class="radio" v-for="t in offer.terms" :key="t.term">
              <label>
                <input type="radio" name="johkumaxTerm" :value="t.term" v-model="term" :disabled="busy" />
                {{ termName(t.term) }}: {{ formatPrice(net(t.price, t.vat)) }} / {{ t.term === 'year' ? _('year') : _('month') }}
              </label>
            </div>
            <p class="help-block">{{ _('Prices exclude VAT.') }}</p>
          </div>
          <div class="form-group" v-if="offer.seat">
            <label for="johkumaxOrderSeats">{{ _('Extra users') }}</label>
            <input type="number" class="form-control" id="johkumaxOrderSeats" min="0" :max="maxSeats" v-model.number="seats" :disabled="busy" />
            <p class="help-block">{{ _('The license includes %s users.').replace('%s', includedSeats) }} {{ _('%s per user per month. You can add users at any time during the term.').replace('%s', formatPrice(net(offer.seat.price, offer.seat.vat))) }}</p>
          </div>
          <div class="form-group" v-if="offer.payments.length > 1">
            <label for="johkumaxPayment">{{ _('Payment method') }}</label>
            <select class="form-control" id="johkumaxPayment" v-model="payment" :disabled="busy">
              <option value="bill">{{ _('Invoice') }}</option>
              <option value="card">{{ _('Card payment') }}</option>
            </select>
          </div>
          <p v-if="selectedTerm">
            <strong>{{ term === 'year' ? _('Total for 12 months') : _('Total per month') }}: {{ formatPrice(orderTotal.net) }}</strong>
            <span class="text-muted"> ({{ _('excluding VAT') }})</span>
          </p>
          <button class="btn btn-success" type="button" v-on:click="orderDialog" :disabled="busy || !selectedTerm || !validSeats(seats)"><i class="fa fa-fw fa-shopping-cart"></i> {{ _('Order Johku Max') }}</button>
        </template>
      </template>
    </template>
  </div>
</template>

<script>

import axios from 'axios'
import errors from '~/lib/errors'
import formats from '~/lib/formats'
import modals from '~/lib/modals'
import { mapGetters } from 'vuex'
import { DateTime } from 'luxon'

const POLL_INTERVAL = 3000
const POLL_LIMIT = 40

export default {
  data () {
    return {
      loading: false,
      loaded: false,
      busy: false,
      offer: null,
      subscription: null,
      seatList: null,
      pending: null,
      lastError: null,
      term: 'year',
      seats: 0,
      payment: 'bill',
      newSeats: 0,
      maxSeats: 500,
      pollTimer: null,
      polls: 0
    }
  },
  mixins: [errors, formats, modals],
  computed: {
    ...mapGetters(['baseDomain', 'shopId', 'apiKey', 'timezone']),
    url () {
      return this.baseDomain + this.shopId + '/johkusubscriptions.json?apikey=' + this.apiKey
    },
    patchUrl () {
      return this.baseDomain + this.shopId + '/johkusubscriptions/max.json?apikey=' + this.apiKey
    },
    seatsUrl () {
      return this.baseDomain + this.shopId + '/johkusubscriptionseats.json?apikey=' + this.apiKey
    },
    includedSeats () {
      return this.offer && this.offer.includedSeats ? this.offer.includedSeats : 0
    },
    isActive () {
      return this.subscription && (this.subscription.status === 'active' || this.isSuspended)
    },
    isSuspended () {
      return this.subscription && this.subscription.status === 'suspended'
    },
    selectedTerm () {
      return this.offer && this.offer.terms ? this.offer.terms.find(t => t.term === this.term) : null
    },
    /** The regular invoice for the chosen term: 12 months for yearly, one month for monthly. Catalogue prices include VAT. */
    orderTotal () {
      if (!this.selectedTerm) {
        return { net: 0, vat: 0, total: 0 }
      }
      const months = this.term === 'year' ? 12 : 1
      const rows = [{ price: this.selectedTerm.price, vat: this.selectedTerm.vat }]
      const seats = Number(this.seats) || 0
      if (this.offer.seat && seats > 0) {
        // The bill row is a rounded unit price per seat times the seats
        rows.push({ price: seats * this.round2(this.offer.seat.price * months), vat: this.offer.seat.vat })
      }
      return this.vatSplit(rows)
    },
    /**
     * The first monthly invoice, for the rest of this month, when the backend gives it (offer term firstInvoice:
     * end is the last billed day). base and seat totals are the bill's unit prices, VAT included.
     */
    firstInvoice () {
      const first = this.term === 'month' && this.selectedTerm ? this.selectedTerm.firstInvoice : null
      if (!first || !first.base) {
        return null
      }
      const seats = first.seat ? Number(this.seats) || 0 : 0
      const vat = first.vat !== undefined ? first.vat : this.selectedTerm.vat
      const rows = [{ price: Number(first.base.total), vat }]
      if (seats > 0) {
        rows.push({ price: seats * Number(first.seat.total), vat })
      }
      return { start: first.start, end: first.end, ...this.vatSplit(rows) }
    },
    seatsChanged () {
      return this.isActive && this.validSeats(this.newSeats) && this.newSeats !== this.currentTargetSeats
    },
    currentTargetSeats () {
      if (!this.subscription) {
        return 0
      }
      return this.subscription.scheduledSeats !== null ? this.subscription.scheduledSeats : this.subscription.seats
    },
    seatChangeEstimate () {
      if (!this.offer || !this.offer.seat || !this.subscription) {
        return 0
      }
      const now = DateTime.now().toSeconds()
      const start = this.subscription.periodStart
      const end = this.subscription.periodEnd
      const share = end > start ? Math.max(0, Math.min(1, (end - now) / (end - start))) : 0
      const months = this.subscription.term === 'year' ? 12 : 1
      return (this.newSeats - this.subscription.seats) * this.net(this.offer.seat.price, this.offer.seat.vat) * months * share
    },
    pendingText () {
      if (!this.pending) {
        return ''
      }
      switch (this.pending.type) {
        case 'order':
          return this._('Your order is being processed. The invoice will be sent to your billing email.')
        case 'seats':
          return this._('The number of users is being updated.')
        default:
          return this._('Your change is being processed.')
      }
    }
  },
  methods: {
    validSeats (n) {
      return Number.isInteger(n) && n >= 0 && n <= this.maxSeats
    },
    termName (term) {
      return term === 'year' ? this._('Yearly') : this._('Monthly')
    },
    formatPrice (amount) {
      return this.formatNumber(amount, 'fi-FI') + ' €'
    },
    round2 (amount) {
      return Math.round((Number(amount) + Number.EPSILON) * 100) / 100
    },
    /** A VAT-inclusive catalogue price without VAT */
    net (price, vat) {
      return this.round2(Number(price) / (1 + (Number(vat) || 0) / 100))
    },
    /**
     * Rows of VAT-inclusive amounts split into net, VAT and total as the bill does it: VAT is worked out once per
     * VAT rate from that rate's gross total (BillModel::getVatBreakdown), so summing rows' nets could be a cent off
     */
    vatSplit (rows) {
      const grossByRate = {}
      for (const row of rows) {
        const rate = Number(row.vat) || 0
        grossByRate[rate] = this.round2((grossByRate[rate] || 0) + this.round2(row.price))
      }
      return Object.entries(grossByRate).reduce((sum, [rate, gross]) => {
        const vat = this.round2(gross - gross / (1 + Number(rate) / 100))
        return {
          net: this.round2(sum.net + gross - vat),
          vat: this.round2(sum.vat + vat),
          total: this.round2(sum.total + gross)
        }
      }, { net: 0, vat: 0, total: 0 })
    },
    /** "2 460,00 € + VAT 25,5 % 627,30 € = 3 087,30 €" */
    vatLine (split) {
      const vat = this.selectedTerm ? this.formatVat(this.selectedTerm.vat, 'fi-FI') + ' %' : ''
      return this._('%1$s + VAT %2$s %3$s = %4$s')
        .replace('%1$s', this.formatPrice(split.net))
        .replace('%2$s', vat)
        .replace('%3$s', this.formatPrice(split.vat))
        .replace('%4$s', this.formatPrice(split.total))
    },
    formatIsoDate (date) {
      return date ? DateTime.fromISO(date).toFormat('d.M.yyyy') : ''
    },
    escapeHtml (text) {
      return String(text).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;')
    },
    formatDate (ts) {
      if (!ts) {
        return ''
      }
      return DateTime.fromSeconds(Number(ts), { zone: this.timezone }).toFormat('d.M.yyyy')
    },
    formatLastDay (ts) {
      if (!ts) {
        return ''
      }
      return DateTime.fromSeconds(Number(ts), { zone: this.timezone }).minus({ days: 1 }).toFormat('d.M.yyyy')
    },
    async load () {
      this.loading = true
      let response
      try {
        response = await axios.get(this.url)
      } catch (e) {
        this.loading = false
        this.showError(e)
        return
      }
      this.loading = false
      this.loaded = true
      this.offer = response.data.offer
      this.subscription = response.data.subscription
      this.pending = response.data.pending
      this.lastError = response.data.lastError
      if (this.offer && this.offer.payments && this.offer.payments.indexOf(this.payment) === -1) {
        this.payment = 'bill'
      }
      if (this.subscription) {
        this.newSeats = this.subscription.scheduledSeats !== null ? this.subscription.scheduledSeats : this.subscription.seats
      }
      if (this.isActive) {
        await this.loadSeats()
      }
      this.schedulePoll()
    },
    async loadSeats () {
      try {
        const response = await axios.get(this.seatsUrl)
        this.seatList = response.data
      } catch (e) {
        this.showError(e)
      }
    },
    formatDateTime (ts) {
      if (!ts) {
        return ''
      }
      return DateTime.fromSeconds(Number(ts), { zone: this.timezone }).toFormat('d.M.yyyy HH:mm')
    },
    removeSeatDialog (seat) {
      this.showDialog({
        title: this._('Remove user'),
        text: this._('%s will lose access to the Johku admin tools within a minute. If they use them again later, they get a place back if there is room.').replace('%s', seat.name || seat.email),
        buttons: [
          {
            title: this._('Cancel'),
            icon: 'close',
            handler: () => {}
          },
          {
            title: this._('Remove'),
            class: 'danger',
            icon: 'user-times',
            handler: () => {
              this.removeSeat(seat)
            }
          }
        ]
      })
    },
    async removeSeat (seat) {
      this.busy = true
      try {
        await axios.delete(this.baseDomain + this.shopId + '/johkusubscriptionseats/' + seat.id + '.json?apikey=' + this.apiKey)
      } catch (e) {
        this.showError(e)
      }
      this.busy = false
      await this.loadSeats()
    },
    schedulePoll () {
      clearTimeout(this.pollTimer)
      if (!this.pending) {
        this.polls = 0
        return
      }
      if (this.polls++ >= POLL_LIMIT) {
        return
      }
      this.pollTimer = setTimeout(() => this.load(), POLL_INTERVAL)
    },
    async send (method, url, data) {
      this.busy = true
      try {
        await axios[method](url, data)
      } catch (e) {
        this.busy = false
        this.showError(e)
        return false
      }
      this.busy = false
      this.polls = 0
      await this.load()
      return true
    },
    orderDialog () {
      // Static dialog fields render HTML, so every line is escaped
      const line = text => ({ type: 'static', text: this.escapeHtml(text) })
      let text
      const fields = []
      if (this.term === 'year') {
        text = this._('You will be invoiced now for the next 12 months:')
        fields.push(line(this.vatLine(this.orderTotal)))
      } else if (this.firstInvoice) {
        text = this._('You will be invoiced now for the rest of this month (%s):')
          .replace('%s', this.formatIsoDate(this.firstInvoice.start) + '–' + this.formatIsoDate(this.firstInvoice.end))
        fields.push(line(this.vatLine(this.firstInvoice)))
        fields.push(line(this._('After that, on the first day of each month:')))
        fields.push(line(this.vatLine(this.orderTotal)))
      } else {
        text = this._('You will be invoiced now for the rest of this month. After that, on the first day of each month:')
        fields.push(line(this.vatLine(this.orderTotal)))
      }
      this.showDialog({
        title: this._('Order Johku Max'),
        text,
        fields,
        buttons: [
          {
            title: this._('Cancel'),
            icon: 'close',
            handler: () => {}
          },
          {
            title: this._('Order'),
            class: 'success',
            icon: 'check',
            handler: () => {
              this.send('post', this.url, { term: this.term, seats: this.seats, payment: this.payment })
            }
          }
        ]
      })
    },
    changeSeats () {
      if (!this.seatsChanged) {
        return
      }
      this.send('patch', this.patchUrl, { seats: this.newSeats })
    },
    cancelDialog () {
      this.showDialog({
        title: this._('Cancel Johku Max'),
        text: this._('Johku Max stays in use until %s and will not be renewed after that. You can undo this until then.').replace('%s', this.formatLastDay(this.subscription.validUntil)),
        buttons: [
          {
            title: this._('Back'),
            icon: 'close',
            handler: () => {}
          },
          {
            title: this._('Cancel Johku Max'),
            class: 'warning',
            icon: 'ban',
            handler: () => {
              this.setCancel(true)
            }
          }
        ]
      })
    },
    setCancel (cancel) {
      this.send('patch', this.patchUrl, { cancel })
    }
  },
  mounted () {
    this.load()
  },
  beforeUnmount () {
    clearTimeout(this.pollTimer)
  }
}
</script>
